import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'shopify_wishlist_items_v1';

/**
 * Global Wishlist Manager Utility
 */
export const Wishlist = {
  get() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.warn('[Wishlist] Error reading from localStorage', e);
      return [];
    }
  },

  save(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      const event = new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: { items, count: items.length },
      });
      document.dispatchEvent(event);
      window.dispatchEvent(event);
    } catch (e) {
      console.warn('[Wishlist] Error writing to localStorage', e);
    }
  },

  has(idOrHandle) {
    if (!idOrHandle) return false;
    const items = this.get();
    const searchVal = String(idOrHandle);
    return items.some(item => String(item.id) === searchVal || item.handle === searchVal);
  },

  add(product) {
    if (!product || (!product.id && !product.handle)) return false;
    const items = this.get();
    const searchVal = String(product.id || product.handle);
    const exists = items.some(item => String(item.id) === searchVal || item.handle === searchVal);
    if (!exists) {
      items.push({
        id: String(product.id),
        handle: product.handle,
        title: product.title || '',
        price: product.price || '',
        compare_at_price: product.compare_at_price || '',
        image: product.image || '',
        url: product.url || `/products/${product.handle}`,
        available: product.available !== false && product.available !== 'false',
        variant_id: product.variant_id ? String(product.variant_id) : '',
        added_at: Date.now()
      });
      this.save(items);
      return true;
    }
    return false;
  },

  remove(idOrHandle) {
    if (!idOrHandle) return false;
    const items = this.get();
    const searchVal = String(idOrHandle);
    const filtered = items.filter(item => String(item.id) !== searchVal && item.handle !== searchVal);
    if (filtered.length !== items.length) {
      this.save(filtered);
      return true;
    }
    return false;
  },

  toggle(product) {
    const searchVal = String(product.id || product.handle);
    if (this.has(searchVal)) {
      this.remove(searchVal);
      return false; // Removed
    } else {
      this.add(product);
      return true; // Added
    }
  },

  count() {
    return this.get().length;
  }
};

/**
 * Custom Element: <wishlist-icon>
 * Header wishlist icon counter
 */
export class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.updateCount();
    document.addEventListener('wishlist:updated', this.onWishlistUpdate);
    window.addEventListener('storage', this.onWishlistUpdate);
    window.addEventListener('pageshow', this.updateCount);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this.onWishlistUpdate);
    window.removeEventListener('storage', this.onWishlistUpdate);
    window.removeEventListener('pageshow', this.updateCount);
  }

  onWishlistUpdate = (e) => {
    const count = e?.detail?.count !== undefined ? e.detail.count : Wishlist.count();
    this.renderBubble(count);
  };

  updateCount = () => {
    this.renderBubble(Wishlist.count());
  };

  renderBubble(count) {
    const bubble = this.querySelector('.wishlist-bubble');
    const countEl = this.querySelector('.wishlist-count');
    if (countEl) {
      countEl.textContent = count;
    }
    if (bubble) {
      if (count > 0) {
        bubble.classList.remove('hidden');
        bubble.classList.remove('visually-hidden');
      } else {
        bubble.classList.add('hidden');
        bubble.classList.add('visually-hidden');
      }
    }
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

/**
 * Custom Element: <wishlist-button>
 * Product card / product page toggle button
 */
export class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }
    this.syncState();
    document.addEventListener('wishlist:updated', this.onWishlistUpdate);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
    document.removeEventListener('wishlist:updated', this.onWishlistUpdate);
  }

  getProductData() {
    return {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      price: this.dataset.productPrice,
      compare_at_price: this.dataset.productCompareAtPrice,
      image: this.dataset.productImage,
      url: this.dataset.productUrl,
      available: this.dataset.productAvailable,
      variant_id: this.dataset.variantId
    };
  }

  handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const product = this.getProductData();
    const added = Wishlist.toggle(product);

    if (this.button) {
      this.button.classList.remove('wishlist-animate-heart');
      // Trigger reflow
      void this.button.offsetWidth;
      this.button.classList.add('wishlist-animate-heart');
    }

    this.updateStateUI(added);
  };

  onWishlistUpdate = () => {
    this.syncState();
  };

  syncState() {
    const product = this.getProductData();
    const idOrHandle = product.id || product.handle;
    const inWishlist = Wishlist.has(idOrHandle);
    this.updateStateUI(inWishlist);
  }

  updateStateUI(inWishlist) {
    if (inWishlist) {
      this.setAttribute('in-wishlist', '');
      const btnText = this.querySelector('.wishlist-btn-text');
      if (btnText) btnText.textContent = 'Remove from Wishlist';
      if (this.button) this.button.setAttribute('aria-label', 'Remove from Wishlist');
    } else {
      this.removeAttribute('in-wishlist');
      const btnText = this.querySelector('.wishlist-btn-text');
      if (btnText) btnText.textContent = 'Add to Wishlist';
      if (this.button) this.button.setAttribute('aria-label', 'Add to Wishlist');
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * Custom Element: <wishlist-page>
 * Renders the Wishlist Page product grid & handles cart operations
 */
export class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.render();
    document.addEventListener('wishlist:updated', this.onWishlistUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this.onWishlistUpdate);
  }

  onWishlistUpdate = () => {
    this.render();
  };

  formatPrice(rawPrice) {
    if (!rawPrice) return '';
    if (typeof rawPrice === 'string' && (rawPrice.includes('$') || rawPrice.includes('€') || rawPrice.includes('£') || rawPrice.includes('₹'))) {
      return rawPrice;
    }
    const num = parseFloat(rawPrice);
    if (isNaN(num)) return rawPrice;
    // Cents or major units
    const amount = num > 100 ? (num / 100).toFixed(2) : num.toFixed(2);
    return `$${amount}`;
  }

  async render() {
    const items = Wishlist.get();
    const countEl = this.querySelector('.wishlist-count-header');
    const container = this.querySelector('.wishlist-grid-container');

    if (countEl) {
      countEl.textContent = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
    }

    if (!container) return;

    if (items.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty-state">
          <svg class="wishlist-empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.78-8.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <h2 class="wishlist-empty-title">Your wishlist is currently empty</h2>
          <p class="wishlist-empty-text">Explore our store and tap the heart icon on any item to save it here.</p>
          <a href="/collections/all" class="button button-primary">Continue Shopping</a>
        </div>
      `;
      return;
    }

    // Auto-fetch variant_id if missing for any item
    for (let item of items) {
      if (!item.variant_id && item.handle) {
        try {
          const res = await fetch(`/products/${item.handle}.js`);
          if (res.ok) {
            const pData = await res.json();
            if (pData.variants && pData.variants.length > 0) {
              item.variant_id = String(pData.variants[0].id);
            }
          }
        } catch (e) {
          console.warn('Could not fetch variant_id for', item.handle);
        }
      }
    }
    Wishlist.save(items);

    let gridHtml = `<div class="wishlist-grid">`;

    items.forEach(item => {
      const priceFormatted = this.formatPrice(item.price);
      const comparePriceFormatted = item.compare_at_price ? this.formatPrice(item.compare_at_price) : '';

      gridHtml += `
        <div class="wishlist-item-card" data-product-id="${item.id}" data-product-handle="${item.handle}">
          <div class="wishlist-item-media">
            <a href="${item.url}">
              ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy" />` : `<div class="resource-card__image-placeholder">${item.title}</div>`}
            </a>
            <button type="button" class="wishlist-item-remove" data-id="${item.id}" data-handle="${item.handle}" title="Remove from wishlist" aria-label="Remove ${item.title}">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="wishlist-item-details">
            <a href="${item.url}" class="wishlist-item-title">${item.title}</a>
            <div class="wishlist-item-price">
              <span>${priceFormatted}</span>
              ${comparePriceFormatted ? `<span class="wishlist-item-compare-price">${comparePriceFormatted}</span>` : ''}
            </div>
            <div class="wishlist-item-actions">
              ${item.variant_id ? `
                <product-form-component
                  data-product-id="${item.id}"
                  on:submit="/handleSubmit"
                  class="quick-add__product-form-component"
                >
                  <form action="/cart/add" method="post" data-type="add-to-cart-form" novalidate="novalidate">
                    <input type="hidden" name="id" value="${item.variant_id}">
                    <input type="hidden" name="quantity" value="1">
                    <add-to-cart-component>
                      <button
                        type="submit"
                        name="add"
                        class="button button-secondary wishlist-add-cart-btn add-to-cart-button"
                      >
                        <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                        </span>
                        Add to Cart
                      </button>
                    </add-to-cart-component>
                  </form>
                </product-form-component>
              ` : `
                <a href="${item.url}" class="button button-secondary wishlist-add-cart-btn">
                  View Product
                </a>
              `}
            </div>
          </div>
        </div>
      `;
    });

    gridHtml += `</div>`;
    container.innerHTML = gridHtml;

    // Attach remove event listeners
    container.querySelectorAll('.wishlist-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = btn.dataset.id;
        const handle = btn.dataset.handle;
        Wishlist.remove(id || handle);
      });
    });
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
